/**
 * Feature Launches — single source of truth.
 *
 * Each entry powers four surfaces:
 *   1. Home-page "What's New" strip (compact teaser).
 *   2. /whats-new index page (full timeline list).
 *   3. /whats-new/{slug} dedicated 1-pager (rich Helmet + JSON-LD).
 *   4. RSS feed + sitemap entries (auto-picked-up by Google News & Bing).
 *
 * SEO discipline:
 *   • `slug` becomes /whats-new/{slug} — keep it stable; never rename after publish.
 *   • `published_at` is ISO-8601 (YYYY-MM-DD) → flows into <time datetime>, NewsArticle
 *     `datePublished`, sitemap `<lastmod>`, and RSS `<pubDate>`.
 *   • `title` should embed the audience + problem + outcome ("for doctors", "verified
 *     patients") to capture long-tail searches.
 *   • `summary` is the meta description / OG description — keep it under 160 chars.
 *   • `keywords` populates <meta name="keywords"> + Article schema `keywords`.
 *
 * To ship a new launch: add an object at the TOP of LAUNCHES, build its dedicated
 * page component (mirror /pages/launch/ConnectOtpLaunch.js), and add the route in
 * App.js. The home strip + /whats-new index + sitemap auto-pick it up.
 */

export const LAUNCHES = [
  {
    slug: 'care-progress-monitor',
    href: '/whats-new/care-progress-monitor',
    published_at: '2026-08-11',
    title: 'Care Progress Monitor — a straight-line view of every visit, medicine, test and improvement milestone on one thread',
    short_title: 'Care Progress Monitor',
    summary: 'A single-glance longitudinal view of a patient\'s condition — visits, medicine changes with dose deltas, tests advised, lab reports received, and doctor-authored improvement milestones on one timeline. Milestones link to the medicines pursuing them; patients see a curated "Your care progress · X of Y goals hit" card inside their DHF viewer.',
    audience: 'both',
    keywords: [
      'longitudinal patient care india',
      'clinical care timeline',
      'chronic disease management dashboard',
      'improvement milestones tracker',
      'medicine adherence timeline',
      'patient progress card india',
      'digital health file progress',
    ],
    badge: 'Progress Monitor',
    isNew: true,
  },
  {
    slug: 'rapid-save-same-day-rule',
    href: '/whats-new/rapid-save-same-day-rule',
    published_at: '2026-08-10',
    title: 'Rapid Save + Same-Day Rule — one-tap walk-in logging plus a hard "check-in today" gate on DHF and Digital Prescriptions',
    short_title: 'Rapid Save + Same-Day Rule',
    summary: 'Doctor logs a walk-in in 4 fields flat — Name, Mobile, Facility, Visit date — with the "Came via" channel auto-locked from the facility type (a Polyclinic can never be misfiled as a Hospital). Digital Health Files and Digital Prescriptions can now only be issued on the same calendar day as the visit — stops the "patient scans QR, goes home, asks 3 days later" abuse loop.',
    audience: 'doctor',
    keywords: [
      'one-tap patient log india',
      'walk-in visit log',
      'clinic check-in qr india',
      'same-day prescription rule',
      'digital health file same day',
      'facility type lock',
      'doctor rapid save',
    ],
    badge: 'Workflow',
    isNew: true,
  },
  {
    slug: 'prescription-platform',
    href: '/whats-new/prescription-platform',
    published_at: '2026-08-04',
    title: 'The DocSite Prescription Platform — a complete rewrite of how doctors write, and pharmacists verify, prescriptions in India',
    short_title: 'The Prescription Platform',
    summary: 'A three-part launch: (1) Auto-Prescriptions issued the moment a Digital Health File is sealed. (2) Smart Medicine Composer with AI molecule/schedule lookup, per-doctor memory and auto-quantity. (3) A public verification tool where any pharmacist or insurer can confirm a DocSite Rx in one scan. Together they replace the paper prescription in every clinic that runs on DocSite.',
    audience: 'both',
    keywords: [
      'digital prescription platform india',
      'ai prescription writer',
      'pharmacist verifiable rx',
      'ehr rx india',
      'signed digital prescription',
      'ai medicine classifier india',
      'e-prescription verification',
    ],
    badge: 'Platform Launch',
    isNew: true,
  },
  {
    slug: 'smart-medicine-composer',
    href: '/whats-new/smart-medicine-composer',
    published_at: '2026-08-04',
    title: 'Smart Medicine Composer — AI molecule lookup, per-doctor memory, auto-quantity, and one-tap form-type',
    short_title: 'Smart Medicine Composer',
    summary: 'The Digital Health File composer just got radically faster. Type a brand name and DocSite auto-fills the molecule, drug class, regulatory schedule (H / H1 / OTC) and default strength. Remembers your dosing habits per brand. Auto-computes total quantity from dose × frequency × duration. Every field editable — your edits are what print on the Rx.',
    audience: 'doctor',
    keywords: [
      'ai medicine composer',
      'brand to molecule ai',
      'ai medicine classifier',
      'doctor dosing memory',
      'auto quantity calculator prescription',
      'schedule h classification',
      'meal timings instructions',
      'dhf composer india',
    ],
    badge: 'Just Launched',
    isNew: true,
  },
  {
    slug: 'docsite-verified-rx',
    href: '/whats-new/docsite-verified-rx',
    published_at: '2026-08-04',
    title: 'DocSite Verified Rx — any pharmacist, insurer or patient can verify a prescription in one scan',
    short_title: 'Public Rx Verify Tool',
    summary: 'A brand new public tool at docsite.care/rx-verify. Drop any DocSite prescription PDF and see instantly whether it is genuine, revoked, or unknown. Cryptographic SHA-256 fingerprint check — no account, no login, no paperwork. Every prescription now carries a scannable QR code that lands directly on this verification page.',
    audience: 'both',
    keywords: [
      'verify prescription qr code',
      'digital rx verification india',
      'sha256 prescription authenticity',
      'pharmacist prescription verify tool',
      'cryptographic prescription check',
      'docsite verified rx',
    ],
    badge: 'Just Launched',
    isNew: true,
  },
  {
    slug: 'auto-prescriptions',
    href: '/whats-new/auto-prescriptions',
    published_at: '2026-08-04',
    title: 'Auto-Prescriptions — every DHF now issues a signed, verifiable digital prescription in one click',
    short_title: 'One-click Digital Prescriptions',
    summary: 'Seal a Digital Health File and DocSite now auto-generates a fully-signed A4 prescription — pixel-perfect, cryptographically verifiable, and instantly downloadable from the same patient link. No extra clicks. No separate SMS. No lost slips.',
    audience: 'both',
    keywords: [
      'digital prescription india',
      'e-prescription automation',
      'verifiable prescription qr code',
      'doctor prescription pdf download',
      'pharmacy dispensing advice',
      'auto-generated prescription',
      'signed digital rx',
      'whatsapp prescription delivery india',
    ],
    badge: 'Just Launched',
    isNew: true,
  },
  {
    slug: 'dhf-v3-conditions-dpdp',
    href: '/whats-new/dhf-v3-conditions-dpdp',
    published_at: '2026-08-01',
    title: 'DocSite Health File just got smarter — safer prescriptions, clearer instructions, world-class privacy',
    short_title: 'A smarter, safer Digital Health File',
    summary: 'The file your doctor shares with you is now clearer, safer, and locked entirely to you. Every health issue gets its own section. Allergies auto-cross-check against new prescriptions. And your whole record is encrypted and sealed to you — no one else can view, edit or use it. Ever.',
    audience: 'both',
    keywords: [
      'digital health file india',
      'safer prescriptions',
      'patient health record privacy',
      'DPDP 2023 patient rights',
      'allergy safety cross-check',
      'clear medicine instructions',
      'longitudinal patient record',
      'health record whatsapp india',
    ],
    badge: 'Just Launched',
    isNew: true,
  },
  {
    slug: 'patient-log-live-dashboard',
    href: '/whats-new/patient-log-live-dashboard',
    published_at: '2026-02-16',
    title: 'Patient Log v2.5: live dashboard intelligence — green-dot alerts, audible chimes, "Visit #" rank, and immutable records',
    short_title: 'Live dashboard + immutable patient records',
    summary: 'Your dashboard now alerts you in real time the moment a patient walks in or a new lead lands — pulsing green dot + count badge on every tab, audible chime on phone/desktop. Every returning patient shows a "Visit #" rank across all your clinics. Records are now immutable (no accidental deletes) and structured First/Middle/Surname capture means full proper names, not casual one-worders.',
    audience: 'doctors',
    keywords: [
      'live dashboard alerts for doctors',
      'audible push notification new patient',
      'visit count returning patient',
      'immutable patient records audit trail',
      'structured patient name capture',
      'patient log local time',
      'doctor dashboard activity counter',
      'clinic check-in dedup proposed update',
    ],
    badge: 'Just Launched',
    isNew: true,
  },
  {
    slug: 'clinic-checkin-qr',
    href: '/whats-new/clinic-checkin-qr',
    published_at: '2026-02-15',
    title: 'New: Clinic Check-In QR — every patient becomes a verified contact + leaves you feedback before walking out',
    short_title: 'Clinic Check-In QR + auto-feedback',
    summary: 'Print one QR. Patient sitting in front of you scans, enters name + mobile in 15 seconds. Their row pops up live on your Patient Log. They get an instant SMS with a 30-second 3-question feedback link to fill before walking out — and a 4th outcome question 7 days later. 20 patients a day = 500 consented contacts in a month, all legit.',
    audience: 'doctors',
    keywords: [
      'clinic check-in QR',
      'patient feedback at clinic',
      'in-clinic patient capture',
      'TQS treatment quality score',
      'consented patient broadcast list India',
      'doctor patient onboarding',
      'verified patient database',
    ],
    badge: 'Just Launched',
    isNew: true,
  },
  {
    slug: 'patient-protected-chat',
    href: '/whats-new/patient-protected-chat',
    published_at: '2026-02-14',
    title: 'Your patient chat is now spam-proof — and brings you real leads',
    short_title: 'Patient-protected Chat + new Chat Inbox',
    summary: 'No more random pings. Every chat now starts with a mobile verification (skipped automatically for your existing patients). New chats from people you don\'t yet know land in a brand-new "Chat Inbox" tab; chats from people already in your Patient Log merge right into their timeline. You can also set your own per-patient chat cap so doctor-shoppers don\'t eat into your day.',
    audience: 'doctors',
    keywords: [
      'doctor patient chat',
      'doctor lead inbox India',
      'secure patient messaging',
      'mobile verified chat',
      'DocSite chat',
      'doctor anti-spam',
      'patient chat limit',
    ],
    badge: 'New',
    isNew: false,
  },
  {
    slug: 'patient-log',
    href: '/whats-new/patient-log',
    published_at: '2026-02-12',
    title: 'One tab to track every patient you saw today',
    short_title: 'Your new daily Patient Log',
    summary: 'Walk-ins, online bookings, Connect-page leads and phone calls now land in one chronological list. Search by name, last-4 of phone, what happened, or notes. Filter by where they came from — 9 channels including referrals from doctors, hospitals, poly-clinics and patients. Default view is the last 3 days; pull older entries with one tap.',
    audience: 'doctors',
    keywords: [
      'doctor patient log India',
      'daily patient list',
      'clinic CRM for doctors',
      'doctor visit tracker',
      'manage patient flow',
      'DocSite patient log',
    ],
    badge: 'Live',
    isNew: false,
  },
  {
    slug: 'credentialed-signup',
    href: '/whats-new/credentialed-signup',
    published_at: '2026-02-12',
    title: 'Sign up with your credentials. Your setup space stays private to you.',
    short_title: 'Credentialed signup + private setup',
    summary: 'Your signup now captures your NMC registration, years of practice, city and primary specialisation in one screen — so your public profile reads credible from minute one. Your dashboard, step-by-step setup guide and 2-page printable now open only after you sign in. The guide can also be emailed to your inbox in one tap.',
    audience: 'doctors',
    keywords: [
      'credentialed doctor signup',
      'NMC registration online India',
      'RMP verified doctor profile',
      'private doctor onboarding',
      'DocSite signup',
      'doctor portfolio India',
    ],
    badge: 'Live',
    isNew: false,
  },
  {
    slug: 'articles-pages',
    href: '/whats-new/articles-pages',
    published_at: '2026-02-10',
    title: 'Your health articles can now show up on Google search',
    short_title: 'Patients find your articles on Google',
    summary: 'Every article you publish now lives on its own dedicated page on your DocSite — written under your name, ranked under your name. When a patient searches "chest pain symptoms in Mumbai", your article can be the first thing they read.',
    audience: 'doctors',
    keywords: [
      'doctor articles patients find me',
      'health articles for patients India',
      'doctor SEO articles',
      'medical insights doctor website',
      'DocSite article pages',
    ],
    badge: 'Live',
    isNew: false,
  },
  {
    slug: 'voice-and-chat',
    href: '/whats-new/voice-and-chat',
    published_at: '2026-02-10',
    title: 'Patients can now call you directly — and your number stays private',
    short_title: 'Direct call without sharing your number',
    summary: 'A patient on your DocSite can tap once and speak to you live — no missed calls, no number shared, no WhatsApp pings on your personal phone. You stay in control: open all day, fixed hours, or hand the line over to your receptionist.',
    audience: 'doctors',
    keywords: [
      'private doctor calls',
      'doctor patient direct call privacy',
      'doctor receptionist forwarding',
      'DocSite voice calls',
      'private chat for doctors',
    ],
    badge: 'Live',
    isNew: false,
  },
  {
    slug: 'pin-fast-login',
    href: '/whats-new/pin-fast-login',
    published_at: '2026-02-07',
    title: 'Sign in instantly with a 6-digit PIN',
    short_title: 'Set a PIN once, sign in in a tap',
    summary: "Set a 6-digit DocSite PIN once and every future login on your phone, laptop or clinic computer becomes instant — no more SMS waits. SMS still kicks in automatically the first time you sign in on a brand-new device, so your account stays as safe as ever.",
    audience: 'doctors',
    keywords: [
      'DocSite PIN login', 'fast doctor login', '6-digit PIN sign in',
      'instant doctor app login India', 'secure clinic login',
    ],
    badge: 'Live',
    isNew: false,
  },
  {
    slug: 'doctor-notify',
    href: '/whats-new/doctor-notify',
    published_at: '2026-02-07',
    title: 'Get pinged the moment a new patient connects',
    short_title: 'Instant ping when a patient reaches you',
    summary: 'When a patient finishes your Connect link, your phone now buzzes instantly — name, mobile, and clinic, in one tap. Email backup if you haven\'t installed DocSite to your phone yet.',
    audience: 'doctors',
    keywords: [
      'instant doctor notification',
      'patient lead alert for doctors',
      'real-time doctor ping',
      'DocSite Connect notification',
      'doctor app notification India',
    ],
    badge: 'Live',
    isNew: false,
  },
  {
    slug: 'connect-otp',
    href: '/whats-new/connect-otp',
    published_at: '2026-02-06',
    title: 'Every new patient is now verified before they reach you',
    short_title: 'Verified-patients-only on your Connect link',
    summary: 'When a new patient opens your Connect link, their phone number is now verified before their message lands with you. Real patients only — no fake leads, no junk entries. Patients on the DocSite app glide through with one tap.',
    audience: 'doctors',
    keywords: [
      'patient verification for doctors',
      'verified patient leads',
      'OTP for doctor websites',
      'no spam in patient inquiries',
      'DocSite Connect link',
      'real patient leads in India',
    ],
    badge: 'Live',
    isNew: false,
  },
];

/**
 * Returns the most-recently-launched entry — used by the home strip and the
 * "fresh dot" indicator on the header.
 */
export function getLatestLaunch() {
  return LAUNCHES[0] || null;
}

/**
 * Returns true if the user hasn't seen the latest launch yet (used for the
 * green dot on the header What's-New link). Tracked in localStorage under
 * `whatsnew_seen_{slug}`.
 */
export function hasUnseenLaunch() {
  const latest = getLatestLaunch();
  if (!latest) return false;
  try {
    return localStorage.getItem(`whatsnew_seen_${latest.slug}`) !== '1';
  } catch { return true; }
}

/**
 * Mark the latest launch as seen — called when the user clicks through to
 * the index page or the launch detail page.
 */
export function markLatestSeen() {
  const latest = getLatestLaunch();
  if (!latest) return;
  try { localStorage.setItem(`whatsnew_seen_${latest.slug}`, '1'); } catch { /* private mode */ }
}
